import AddIcon from '@mui/icons-material/Add';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import {
    Box,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    useGetBookingSetMenusQuery,
    useGetJobsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../../shared/AuthRoles';
import { BookingSetMenuStatus } from '../../../enums/set-menu';
import { BookingSetMenu } from '../../../types/set-menu';
import ContractorModal from '../../Reservations/ReservationContractors/ContractorModal';
import ContractorRecurringModal from '../../Reservations/ReservationContractors/ContractorRecurringModal';
import JobList from './JobList';
import JobPreview from './JobPreview';

type JobStatus =
    | 'pending'
    | 'accepted'
    | 'completed'
    | 'declined'
    | 'cancelled'
    | 'pendingCompletion';

const Jobs = () => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedJobId = searchParams.get('selectedJob') ?? undefined;
    const providerType = searchParams.get('provider-type');
    const statusParam = searchParams.get('status') as JobStatus | null;
    const [tab, setTab] = useState<JobStatus>(statusParam ?? 'pending');
    const [newJobOpen, setNewJobOpen] = useState<boolean>(false);
    const [newRecurringJobOpen, setNewRecurringJobOpen] =
        useState<boolean>(false);
    const loggedInUser = UserRole();

    const bookingSetMenuStatus =
        tab === 'accepted'
            ? BookingSetMenuStatus.Active
            : tab === 'declined'
              ? BookingSetMenuStatus.Declined
              : tab === 'cancelled'
                ? BookingSetMenuStatus.Cancelled
                : tab === 'completed'
                  ? BookingSetMenuStatus.Completed
                  : undefined;

    const { data: jobs } = useGetJobsQuery({ status: tab });

    const { data: bookingSetMenus } = useGetBookingSetMenusQuery(
        { status: bookingSetMenuStatus },
        {
            skip: !loggedInUser?.company || !bookingSetMenuStatus,
            refetchOnMountOrArgChange: true,
        },
    );
    //We should set bookingSetMenuJobs to [] when bookingSetMenuStatus is undefined otherwise the previous data will be shown
    const bookingSetMenuJobs = bookingSetMenuStatus ? bookingSetMenus : [];

    const allData: (Job | BookingSetMenu)[] = [
        ...(jobs ?? []),
        ...(bookingSetMenuJobs ?? []),
    ];

    const numericId = useMemo<number | null>(
        () =>
            selectedJobId && /^\d+$/.test(selectedJobId)
                ? Number(selectedJobId)
                : null,
        [selectedJobId],
    );
    const visibleJobIds = allData.map((job) => ({
        id: job.id,
        providerType: job.providerType,
    }));

    const theme = useTheme();

    const handleTabChange = (_: any, value: JobStatus) => {
        setTab(value);
        const next = new URLSearchParams(searchParams);
        next.set('status', value);
        next.delete('selectedJob');
        setSearchParams(next, { replace: true });
    };

    const tabs: { value: JobStatus; label: string }[] = [
        { value: 'pending', label: 'Pending' },
        { value: 'accepted', label: 'Upcoming' },
        { value: 'pendingCompletion', label: 'Pending Completion' },
        { value: 'completed', label: 'Completed' },
        { value: 'declined', label: 'Declined' },
        { value: 'cancelled', label: 'Cancelled' },
    ];

    const initialIndex = visibleJobIds.findIndex(
        (job) =>
            job.id === Number(selectedJobId) &&
            job.providerType === providerType,
    );

    return (
        <>
            <Stack sx={{ pt: '30px', gap: 4 }}>
                <div>
                    <Typography color='text.primary' sx={{ fontSize: '14px' }}>
                        Job Requests allow you to manage both your own service
                        jobs and contractor jobs in one place. View jobs created
                        from your predefined service menus as well as requests
                        assigned to external contractors.
                    </Typography>
                    <Typography color='text.primary' sx={{ fontSize: '14px' }}>
                        You can create a new one-off or recurring contractor job
                        request. Contractors will receive the request and can
                        accept or decline the work for the specified location
                        and date.
                    </Typography>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
                        width='100%'
                        spacing={1}
                        sx={{ paddingTop: isMobile ? '10px' : '0px' }}
                    >
                        <CustomButton
                            type='button'
                            variant='contained'
                            color='primary'
                            onClick={() => setNewJobOpen(true)}
                            fullWidth={isMobile}
                        >
                            <AddIcon />
                            <span style={{ marginLeft: '5px' }}>New Job</span>
                        </CustomButton>
                        <CustomButton
                            type='button'
                            variant='contained'
                            color='primary'
                            onClick={() => setNewRecurringJobOpen(true)}
                            fullWidth={isMobile}
                        >
                            <EventRepeatIcon />
                            <span style={{ marginLeft: '5px' }}>
                                New Recurring Job
                            </span>
                        </CustomButton>
                    </Stack>

                    <Stack spacing={1}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Tabs
                                value={tab}
                                onChange={handleTabChange}
                                variant='scrollable'
                                scrollButtons='auto'
                                sx={{ alignItems: 'flex-start' }}
                                TabIndicatorProps={{
                                    sx: {
                                        left: '8%',
                                        transform: 'translateX(-8%)',
                                        bottom: 0,
                                        borderRadius: '5px',
                                        backgroundColor:
                                            theme.palette.info.main,
                                        transition: 'all 150ms ease',
                                    },
                                }}
                            >
                                {tabs.map(({ value, label }) => (
                                    <Tab
                                        key={value}
                                        value={value}
                                        label={
                                            <Box
                                                sx={{
                                                    textAlign: 'left',
                                                    width: '100%',
                                                }}
                                            >
                                                {label}
                                            </Box>
                                        }
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            minWidth: 'fit-content',
                                            pl: 0,
                                        }}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        <JobList jobs={allData ?? []} />
                    </Stack>
                </div>

                <ContractorModal
                    open={newJobOpen}
                    onClose={() => setNewJobOpen(false)}
                />

                <ContractorRecurringModal
                    open={newRecurringJobOpen}
                    isEditing={false}
                    onClose={() => setNewRecurringJobOpen(false)}
                />
                <JobPreview
                    open={numericId != null}
                    id={numericId}
                    jobIds={
                        visibleJobIds as {
                            id: number;
                            providerType: ProviderType;
                        }[]
                    }
                    initialIndex={initialIndex}
                    onClose={() => {
                        const next = new URLSearchParams(searchParams);
                        next.delete('selectedJob');
                        next.delete('provider-type');
                        setSearchParams(next, { replace: true });
                    }}
                />
            </Stack>
        </>
    );
};

export default Jobs;
